// Public presets list + fetch
import { apiFetchJSON } from '../../core/api.js';
import {
  calcDiscountPercent,
  escapeAttr,
  formatPriceWithRub,
  formatRub,
  parsePriceNumber
} from '../../core/format.js';
import {
  addToCartFromPreset,
  pruneCartAgainstPresets,
  syncPresetCartButtonsUi
} from '../cart/cart.js';
import { showPresetDetail } from './detail.js';
import { presetHasConfigurator } from '../../lib/template-config.js';
import { lazyImageHtml, refreshLazyImages } from '../../core/lazy-image.js';
import type { FetchPresetsOptions, PresetItem, PresetsApiResponse } from '../../types/api.js';
import { isCustomerActionLive } from '../../app/feature-flags.js';
import { handleCustomerActionGate } from '../../app/beta-wip.js';
import { showView } from '../../app/navigation.js';

let lastPresetsList: PresetItem[] = [];
let presetsLoadedOnce = false;

export function getLastPresetsList(): PresetItem[] {
  return lastPresetsList;
}

export function setLastPresetsList(items: PresetItem[] | null | undefined): void {
  lastPresetsList = items || [];
}

export function getPresetsLoadedOnce(): boolean {
  return presetsLoadedOnce;
}

export function setPresetsLoadedOnce(value: boolean): void {
  presetsLoadedOnce = !!value;
}

export async function fetchPresets(opts?: FetchPresetsOptions): Promise<PresetItem[]> {
  try {
    const query = opts?.includeHidden ? '?includeHidden=1' : '';
    const data = await apiFetchJSON<PresetsApiResponse | null>(`/api/presets${query}`).catch(
      () => null
    );
    if (!data || !data.ok || !Array.isArray(data.items)) return [];
    return data.items;
  } catch {
    return [];
  }
}

export async function updateMainPresetsPrice(): Promise<void> {
  if (!isCustomerActionLive('presets')) return;
  try {
    const priceEl = document.querySelector(
      '.service-card[data-action="presets"] .service-price'
    );
    if (!priceEl) return;
    const items = await fetchPresets();
    if (!items.length) {
      priceEl.textContent = 'временно нет в наличии';
      return;
    }
    let min: number | null = null;
    items.forEach((it) => {
      const v = parsePriceNumber(it?.price);
      if (v == null) return;
      if (min == null || v < min) min = v;
    });
    if (min == null) {
      priceEl.textContent = 'временно нет в наличии';
      return;
    }
    priceEl.textContent = 'от ' + formatRub(min);
  } catch {
    /* ignore */
  }
}

function renderPresetCard(item: PresetItem): string {
  const priceText = formatPriceWithRub(item.price);
  const oldPriceText = formatPriceWithRub(item.oldPrice);
  const discountPercent = calcDiscountPercent(item.price, item.oldPrice);
  const title = escapeAttr(item.title || '');
  const subtitle = String(item.shortDescription || '').trim();
  let html =
    '<article class="preset-announcement-card" data-preset-id="' +
    escapeAttr(item.id) +
    '" tabindex="0" role="button">';
  html += '<div class="preset-card-image-wrap">';
  if (item.image && item.image.length > 0) {
    html += lazyImageHtml(item.image, { loading: 'lazy' });
  } else {
    html +=
      '<div style="width:100%;height:100%;background:#242424;display:flex;align-items:center;justify-content:center;color:#8e8e93;font-size:12px;">Нет фото</div>';
  }
  if (item.delivery) {
    html += '<span class="preset-card-delivery-icon" aria-hidden="true">🚚</span>';
  }
  html += '</div>';
  html += '<div class="preset-card-info">';
  html += '<div class="preset-card-copy">';
  html += '<p class="preset-card-title">' + title + '</p>';
  if (subtitle) {
    html += '<p class="preset-card-extra">' + escapeAttr(subtitle) + '</p>';
  }
  html += '</div>';
  html += '<div class="preset-card-price-row">';
  html +=
    '<p class="preset-card-price' +
    (priceText ? '' : ' preset-card-price-empty') +
    '">' +
    escapeAttr(priceText || '') +
    '</p>';
  if (oldPriceText) html += '<p class="preset-card-old-price">' + escapeAttr(oldPriceText) + '</p>';
  if (discountPercent) html += '<p class="preset-card-discount">-' + discountPercent + '%</p>';
  html += '</div>';
  const hasConfig = presetHasConfigurator(item);
  html +=
    '<button type="button" class="preset-card-buy-btn' +
    (hasConfig ? ' preset-card-buy-btn--config' : '') +
    '">' +
    escapeAttr(hasConfig ? 'Настроить' : 'В корзину') +
    '</button>';
  html += '</div></article>';
  return html;
}

const loadingPresetsHtml =
  '<div class="list-loading"><div class="list-loading-spinner"></div><span>Загрузка сборок...</span></div>';

export async function loadPublicPresets(): Promise<void> {
  if (!handleCustomerActionGate('presets')) {
    showView('view-main');
    return;
  }
  const listEl = document.getElementById('presets-list');
  if (!listEl) return;
  listEl.classList.add('preset-list-loading');
  listEl.classList.remove('preset-list-empty');
  listEl.innerHTML = loadingPresetsHtml;
  const itemsRaw = await fetchPresets();
  const items = (itemsRaw || []).filter((item) => !item || item.hidden !== true);
  lastPresetsList = items;
  presetsLoadedOnce = true;
  try {
    pruneCartAgainstPresets();
  } catch {
    /* ignore */
  }
  updateMainPresetsPrice();
  if (!items.length) {
    listEl.classList.remove('preset-list-loading');
    listEl.classList.add('preset-list-empty');
    listEl.innerHTML = '<p class="consult-text">Пока нет готовых сборок.</p>';
    return;
  }
  listEl.classList.remove('preset-list-loading');
  listEl.classList.remove('preset-list-empty');
  listEl.innerHTML = items.map(renderPresetCard).join('');
  refreshLazyImages(listEl);
  listEl.querySelectorAll('.preset-announcement-card').forEach((card) => {
    const id = card.getAttribute('data-preset-id');
    const item = lastPresetsList.find((p) => String(p.id) === String(id));
    if (!item) return;
    const preset = item;
    function openDetail(): void {
      showPresetDetail(preset);
    }
    card.addEventListener('click', openDetail);
    card.addEventListener('keydown', (e: Event) => {
      const ke = e as KeyboardEvent;
      if (ke.key === 'Enter' || ke.key === ' ') {
        ke.preventDefault();
        openDetail();
      }
    });
  });
  listEl.querySelectorAll('.preset-card-buy-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const card = btn.closest('.preset-announcement-card');
      const pid = card?.getAttribute('data-preset-id');
      const presetItem = lastPresetsList.find((p) => String(p.id) === String(pid));
      if (!presetItem) return;
      if (presetHasConfigurator(presetItem)) {
        showPresetDetail(presetItem);
        return;
      }
      addToCartFromPreset(presetItem);
    });
  });
  syncPresetCartButtonsUi();
}
